import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { ImageService } from '@/app/services/image/image.service';
import {
  BriefProgress,
  BriefType,
  briefProgress,
  SmartBriefConversation,
  SmartBriefDraft,
} from '../conversation/smart-brief-conversation';
import { liveBriefSections, LiveBriefCard, LiveBriefSection } from './live-brief.groups';
import { UgcCreatePanel } from '../ugc-create-panel/ugc-create-panel';

@Component({
  selector: 'cbss-live-brief',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UgcCreatePanel],
  templateUrl: './live-brief.html',
})
export class LiveBrief {
  readonly briefType = input<BriefType | null>(null);
  readonly step = input<SmartBriefConversation['step']>('intro');
  readonly draft = input<SmartBriefDraft | null>(null);
  readonly savedCampaignId = input<string | null>(null);
  readonly savedPricingModelId = input<string | null>(null);
  readonly canPublish = input(false);
  readonly askedDos = input(false);
  readonly askedDonts = input(false);
  readonly editPieces = output<void>();
  readonly changeProduct = output<void>();
  readonly addProduct = output<void>();
  readonly publish = output<void>();

  private readonly imageService = inject(ImageService);
  private readonly openPoints = signal<ReadonlySet<string>>(new Set());
  private readonly pointPreview = 3;

  headerType(briefType: BriefType): string {
    if (briefType === 'ugc') {
      return 'UGC';
    }
    if (briefType === 'screening') {
      return 'Social Screening';
    }
    return 'Social Monitoring';
  }

  progress(): BriefProgress | null {
    return briefProgress(this.briefType(), this.step(), this.draft()?.reportType ?? null, {
      dos: this.askedDos() || this.step() === 'dos',
      donts: this.askedDonts() || this.step() === 'donts',
    });
  }

  sections(): LiveBriefSection[] {
    const draft = this.draft();
    return draft ? liveBriefSections(draft) : [];
  }

  productImageUrl(image: string): string {
    return this.imageService.getImageSource(image, 'gallery');
  }

  visibleLines(item: LiveBriefCard): string[] {
    if (this.hasMorePoints(item) && !this.pointsOpen(item.id)) {
      return item.lines.slice(0, this.pointPreview);
    }
    return item.lines;
  }

  hasMorePoints(item: LiveBriefCard): boolean {
    return (item.id === 'requirements' || item.id === 'restrictions') && item.lines.length > this.pointPreview;
  }

  pointsOpen(id: string): boolean {
    return this.openPoints().has(id);
  }

  togglePoints(id: string): void {
    this.openPoints.update((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }
}
